import { HttpClient, HttpHeaders, HttpErrorResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, tap, map } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { JwtHelperService } from "@auth0/angular-jwt";
import { IAppStore } from "../iappstore";
import { Store } from "@ngrx/store";
import { SetTokenExpiredAction, LoggedOutAction, UnauthorizedAction } from "../actions/azureauthentication.action";
import { SetNotFoundAction, SetServerErrorAction, SetBadRequestAction } from "../actions/errorstatus.action";
import { IBusinessFunction } from "../interfaces/ibusinessfunction";
import { ShowLoadingAction, HideLoadingAction } from "../actions/loading.action";

/**
 * @whatItDoes A base class for services that can be used to perform http requests.
 * If any errors occur, it shows the error messages as a toast
 *
 * @howToUse To use extend the BaseService
 */
export class BaseService {
    httpClient: HttpClient;
    headers: HttpHeaders;
    jwtHelper = new JwtHelperService();

    constructor(httpClient: HttpClient,
        private toastr: ToastrService,
        private store: Store<IAppStore>) {
        this.httpClient = httpClient;
        this.headers = new HttpHeaders({
            'Content-Type': 'application/json;charset=utf-8'
        });
    }

    /**
     * Makes a http get request to the url passed in, returns an
     * observable of the response
     * @param url The url to make the request to
     */
    get(url: string): Observable<any> {
        return this.intercept(this.httpClient.get(url, { headers: this.getAuthHeaders() }));
    }

    /**
     * Makes a http get request to the url passed in with the specified content type,
     * returns an observable of the response
     * @param url
     * @param contentType
     */
    getWithContentType(url: string, contentType: string): Observable<any> {
        const headers = this.getAuthHeaders().set('Content-Type', contentType);
        return this.intercept(this.httpClient.get(url, { headers, responseType: 'blob' }));
    }

    /**
     * Makes a http get request to the url passed in,
     * returns an observable of the text response
     * @param url The url to make the request to
     */
    getText(url: string): Observable<string> {
        return this.intercept(this.httpClient.get(url, { headers: this.getAuthHeaders(), responseType: 'text' }));
    }

    /**
     * Makes a http post request to the url passed in with the data specified,
     * returns an observable of the response
     * @param url The url to make the request to
     * @param data The data to post
     */
    post(url: string, data: any): Observable<any> {
        return this.intercept(this.httpClient.post(url, data, { headers: this.getAuthHeaders() }));
    }

    /**
     * Makes a http post request to the url passed in with the data specified,
     * returns an observable of the response (for file uploads)
     * @param url The url to make the request to
     * @param data The data to post
     */
    postFile(url: string, data: any): Observable<any> {
        // Remove content-type for file uploads to let browser set it
        const headers = this.getAuthHeaders().delete('Content-Type');
        return this.intercept(this.httpClient.post(url, data, { headers }));
    }

    /**
     * Makes a http post request to the url passed in with the data specified,
     * returns a blob observable of the response
     * @param url The url to make the request to
     * @param data The data to post
     */
    postForFile(url: string, data: any): Observable<any> {
        const headers = this.getAuthHeaders();
        return this.intercept(this.httpClient.post(url, data, { headers, responseType: 'blob' }));
    }

    /**
     * Makes a http put request to the url passed in with the data specified,
     * returns an observable of the response
     * @param url The url to make the request to
     * @param data The data to put
     */
    put(url: string, data: any): Observable<any> {
        return this.intercept(this.httpClient.put(url, data, { headers: this.getAuthHeaders() }));
    }

    /**
     * Makes a http delete request to the url passed in,
     * returns an observable of the response
     * @param url The url to make the request to
     */
    delete(url: string): Observable<any> {
        return this.intercept(this.httpClient.delete(url, { headers: this.getAuthHeaders() }));
    }

    private getAuthHeaders(): HttpHeaders {
        this.store.dispatch(new ShowLoadingAction());

        let headers = this.headers;
        let accessToken: string = null;
        let selectedBusinessFunction: IBusinessFunction = null;
        let userRoles: string[] = [];

        this.store.select(e => e.azureAuthentication).subscribe(e => accessToken = e.token);
        this.store.select(e => e.user).subscribe(e => {
            if (e.selectedBusinessFunction) {
                selectedBusinessFunction = e.selectedBusinessFunction;
            }
            if (e.roles && e.roles.length > 0) {
                userRoles = e.roles;
            }
        });

        // set auth token if available and check it hasn't expired
        if (accessToken != null) {
            // check token hasn't expired
            let tokenExpirationDate: Date = this.jwtHelper.getTokenExpirationDate(accessToken);
            if (tokenExpirationDate && tokenExpirationDate < new Date()) {
                this.store.dispatch(new SetTokenExpiredAction());
            }

            headers = headers.set('Authorization', `Bearer ${accessToken}`);
        }

        if (selectedBusinessFunction && selectedBusinessFunction.id != null) {
            headers = headers.set('BusinessFunctionId', selectedBusinessFunction.id.toString());
        }

        // Add user roles for security validation
        if (userRoles.length > 0) {
            headers = headers.set('role', userRoles.join(','));
        }

        return headers;
    }

    /**
     * @internal checks for error messages that may occur from requests.
     * If a 401 error occurs, the user will be redirected, for other errors
     * toasts will be shown to the user
     * @param observable The response from the request made
     */
    private intercept(observable: Observable<any>): Observable<any> {
        return observable.pipe(
            tap(() => {
                // Defer store dispatch to avoid NG0100 errors during change detection
                queueMicrotask(() => {
                    this.store.dispatch(new HideLoadingAction());
                });
            }),
            catchError((err: HttpErrorResponse) => {
                queueMicrotask(() => {
                    this.store.dispatch(new HideLoadingAction());
                });

                if (err.status === 401 || err.status === 403) {
                    this.store.dispatch(new UnauthorizedAction());
                } else if (err.status === 404) {
                    this.store.dispatch(new SetNotFoundAction());
                } else if (err.status === 500) {
                    this.store.dispatch(new SetServerErrorAction());
                } else if (err.status === 400) {
                    this.store.dispatch(new SetBadRequestAction());
                    if (err.error && err.error.message) {
                        setTimeout(() => this.toastr.error(err.error.message), 0);
                    }
                    else if (err.error.message == undefined && typeof (err.error) === "string") {
                        setTimeout(() => this.toastr.error(err.error), 0);
                    } else {
                        setTimeout(() => this.toastr.error('An error occurred'), 0);
                    }
                } else {
                    if (err.error && err.error.message) {
                        setTimeout(() => this.toastr.error(err.error.message), 0);
                    }
                    else if (err.error.message == undefined && typeof (err.error) === "string") {
                        setTimeout(() => this.toastr.error(err.error), 0);
                    } else {
                        setTimeout(() => this.toastr.error('An error occurred'), 0);
                    }
                }

                return throwError(err);
            })
        );
    }

    protected getJWTToken(): string | null {
        let accessToken: string = null;
        this.store.select(e => e.azureAuthentication).subscribe(e => accessToken = e.token);
        return accessToken;
    }

    /**
     * Sends a file upload request
     */
    postFileWithContent(url: string, formData: FormData): Observable<any> {
        const headers = this.getAuthHeaders().delete('Content-Type');
        // Remove Content-Type header for file uploads - let browser set it with boundary

        return this.intercept(this.httpClient.post(url, formData, {
            headers: headers,
            responseType: 'blob'
        }));
    }
}
